import { Fragment, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpenIcon, FlagIcon } from "@heroicons/react/20/solid";

export default function QuestionNavPanel({
  questions,
  currentIndex,
  answeredMap,
  testSections,
  onNavigate,
  flaggedSet = new Set(),
  runByIndex,
}) {
  const { t } = useTranslation();
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const captionId = useId();

  const answeredCount = Object.keys(answeredMap).length;
  const flaggedCount = flaggedSet.size;

  const getButtonClass = (index) => {
    const q = questions[index];
    if (!q) return "bg-white text-slate-600 ring-1 ring-inset ring-slate-200";

    if (index === currentIndex) {
      return "bg-blue-600 text-white shadow-sm shadow-blue-600/30 hover:bg-blue-700";
    }
    if (answeredMap[q.attempt_question_uuid]) {
      return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100";
    }
    return "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:ring-slate-300";
  };

  const isFlagged = (index) => {
    const q = questions[index];
    return q && flaggedSet.has(q.attempt_question_uuid);
  };

  const filterIndices = (indices) => {
    if (!showFlaggedOnly) return indices;
    return indices.filter((i) => isFlagged(i));
  };

  const renderGrid = (indices) => {
    const filtered = filterIndices(indices);
    if (filtered.length === 0) {
      return (
        <p className="py-1 text-xs italic text-slate-400">—</p>
      );
    }
    return (
      <div className="grid grid-cols-5 gap-2">
        {filtered.map((globalIdx) => (
          <button
            key={globalIdx}
            onClick={() => onNavigate(globalIdx)}
            aria-label={t("exam_taking.go_to_question", { number: globalIdx + 1 })}
            title={t("exam_taking.question_number", { number: globalIdx + 1 })}
            className={`h-9 w-full rounded-lg text-sm font-medium tabular-nums transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${getButtonClass(globalIdx)} ${
              isFlagged(globalIdx) ? "outline outline-2 outline-offset-2 outline-amber-400" : ""
            }`}
          >
            {globalIdx + 1}
          </button>
        ))}
      </div>
    );
  };

  // Questions on one passage (a run, see passageRuns) are boxed together under "Passage · 3–5".
  const renderIndices = (indices) => {
    const segments = [];
    indices.forEach((index) => {
      const run = runByIndex?.[index] || null;
      const last = segments[segments.length - 1];
      if (last && last.run === run) last.indices.push(index);
      else segments.push({ run, indices: [index] });
    });
    if (!segments.some((segment) => segment.run)) return renderGrid(indices);

    const shown = segments.filter((segment) => filterIndices(segment.indices).length > 0);
    if (shown.length === 0) return renderGrid([]);
    return (
      <div className="flex flex-col gap-2">
        {shown.map(({ run, indices: segmentIndices }) => {
          if (!run) return <Fragment key={segmentIndices[0]}>{renderGrid(segmentIndices)}</Fragment>;
          const id = `${captionId}${run.start}`;
          return (
            <div
              key={run.key}
              role="group"
              aria-labelledby={id}
              className="-mx-1.5 rounded-xl bg-indigo-50/70 p-1.5 ring-1 ring-indigo-100"
            >
              <p id={id} className="mb-1.5 flex items-center gap-1 px-0.5 text-[11px] font-medium text-indigo-700">
                <BookOpenIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">
                  {run.start === run.end
                    ? t("exam_taking.passage.nav_caption_single", { number: run.start + 1 })
                    : t("exam_taking.passage.nav_caption", { from: run.start + 1, to: run.end + 1 })}
                </span>
              </p>
              {renderGrid(segmentIndices)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {flaggedCount > 0 && (
        <button
          type="button"
          onClick={() => setShowFlaggedOnly((prev) => !prev)}
          className={`inline-flex items-center gap-1.5 self-start rounded-lg px-2.5 py-1.5 text-left text-xs font-medium ring-1 ring-inset transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
            showFlaggedOnly
              ? "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
              : "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100"
          }`}
        >
          <FlagIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {showFlaggedOnly
            ? t("exam_taking.show_all")
            : `${t("exam_taking.show_flagged")} (${flaggedCount})`}
        </button>
      )}

      {testSections.length > 0
        ? testSections.map((section) => (
            <div key={section.testUuid}>
              <p
                className="mb-2.5 truncate text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                title={section.testName}
              >
                {section.testName}
              </p>
              {renderIndices(section.questionIndices)}
            </div>
          ))
        : renderIndices(questions.map((_, i) => i))}

      <div className="border-t border-slate-200 pt-4 text-sm text-slate-600">
        {t("exam_taking.answered")}:{" "}
        <span className="font-semibold tabular-nums text-slate-900">
          {answeredCount} /{" "}
          {questions.length}
        </span>
      </div>
    </div>
  );
}
